import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Palette, Globe, BarChart3, Kanban } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesSection = ({ onGetInTouchClick }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping
  const iconMap = {
    'Palette': <Palette size={32} />,
    'Globe': <Globe size={32} />,
    'BarChart3': <BarChart3 size={32} />,
    'Kanban': <Kanban size={32} />
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/services`);
        setServices(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching services:', error);
        setError('Failed to load services');
        setLoading(false);
        
        // Fallback to mock data if API fails
        setServices([
          {
            id: 1,
            title: "Logo Design",
            description: "Create memorable brand identities that resonate with your target audience and stand out in the marketplace.",
            icon: "Palette",
            image_url: "https://images.unsplash.com/photo-1457305237443-44c3d5a30b89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NTg0MTA4NzJ8MA&ixlib=rb-4.1.0&q=85",
            features: ["Brand Identity Development", "Color Psychology Application", "Scalable Vector Design", "Multi-format Delivery"]
          },
          {
            id: 2,
            title: "Website Development",
            description: "Build responsive, user-friendly websites that drive conversions and grow your business online presence.",
            icon: "Globe",
            image_url: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb2Rpbmd8ZW58MHx8fHwxNzU4NDEyMTM5fDA&ixlib=rb-4.1.0&q=85",
            features: ["Responsive Design", "SEO Optimization", "E-commerce Integration", "Content Management Systems"]
          },
          {
            id: 3,
            title: "Monitoring & Analytics",
            description: "Track your digital performance with comprehensive monitoring solutions and actionable insights.",
            icon: "BarChart3",
            image_url: "https://images.unsplash.com/photo-1593720213428-28a5b9e94613?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NTg0MTA4NzJ8MA&ixlib=rb-4.1.0&q=85",
            features: ["Real-time Monitoring", "Performance Analytics", "Security Tracking", "Automated Reporting"]
          },
          {
            id: 4,
            title: "Project Management",
            description: "Streamline your business operations with custom project management solutions and consulting services.",
            icon: "Kanban",
            image_url: "https://images.unsplash.com/photo-1547658719-da2b51169166?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NTg0MTA4NzJ8MA&ixlib=rb-4.1.0&q=85",
            features: ["Workflow Optimization", "Team Collaboration Tools", "Progress Tracking", "Resource Management"]
          }
        ]);
      }
    };

    fetchServices();
  }, []);

  if (loading) {
    return (
      <section id="services" className="section">
        <div className="container">
          <div className="text-center">
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Loading services...
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="section">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="heading-1 mb-6">Our Services</h2>
          <p className="body-medium max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Comprehensive digital solutions designed to elevate your small business and drive sustainable growth.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div key={service.id} className={`service-card ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
              <div className="service-card-content">
                <div>
                  <div className="flex items-center gap-3 mb-4" style={{ color: 'var(--brand-primary)' }}>
                    {iconMap[service.icon] || <Globe size={32} />}
                    <h3 className="service-card-title">{service.title}</h3>
                  </div>
                  
                  <p className="body-small mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="body-small flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  onClick={onGetInTouchClick}
                  className="btn-secondary btn-hover-scale w-full"
                >
                  {service.id === "1" ? 'Get My Logo' : service.id === "2" ? 'Build My Website' : service.id === "3" ? 'Learn More' : 'Discuss Project'}
                </Button>
              </div>
              
              <div className="service-card-image">
                <img 
                  src={service.image_url} 
                  alt={`${service.title} - Professional digital services by LOVEYUHLIFE. ${service.description}`}
                  loading="lazy"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;